/**
 * The Voice settings page (`VoiceModeSettings`) edits the assistant voice,
 * recording and cue timing, and speech-detection thresholds in `settingsStore`.
 * `selectAndSampleVoice` plays voice previews.
 */
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Icons } from '@/assets/icons/icons';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Slider } from '@/components/ui/Slider';
import {
  selectAndSampleVoice,
  stopVoiceSample,
} from '@/features/chat/voice/read-aloud.store';
import {
  getResponseAudioVoiceName,
  getAvailableResponseAudioVoices,
} from '@/features/chat/voice/response-audio-voices';
import { settingsStore, VOICE_MODE_RANGES } from '@/stores/settings.store';
import { userStore } from '@/stores/user.store';

import { NumericInput, type NumericInputProps } from '../numeric-input';
import { SettingsSection } from '../settings-section';

type NumericSettingProps = NumericInputProps & {
  label: string;
  description: string;
  searchId: string;
};

const NumericSetting = ({
  label,
  description,
  searchId,
  ...inputProps
}: NumericSettingProps) => (
  <SettingsSection.Item searchId={searchId}>
    <SettingsSection.ItemHeader>
      <SettingsSection.ItemLabel>{label}</SettingsSection.ItemLabel>
      <SettingsSection.ItemDescription>
        {description}
      </SettingsSection.ItemDescription>
    </SettingsSection.ItemHeader>
    <NumericInput
      {...inputProps}
      disabled={!settingsStore.speechFeaturesEnabled}
    />
  </SettingsSection.Item>
);

const FrequencyRangeSetting = () => {
  const { minimumVoiceFrequencyHz, maximumVoiceFrequencyHz } =
    settingsStore.voiceModeDetection;

  return (
    <SettingsSection.Item searchId="voice-mode.speech-frequency-range">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Speech Frequency Range
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Frequencies whose energy counts toward the speech score
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <YStack className="w-72 shrink-0 gap-2">
        <Slider
          aria-label="Speech frequency range"
          disabled={!settingsStore.speechFeaturesEnabled}
          value={[minimumVoiceFrequencyHz, maximumVoiceFrequencyHz]}
          min={VOICE_MODE_RANGES.voiceFrequencyHz.min}
          max={VOICE_MODE_RANGES.voiceFrequencyHz.max}
          step={5}
          minStepsBetweenThumbs={10}
          onValueChange={([minimum, maximum]) => {
            settingsStore.setVoiceModeDetection({
              minimumVoiceFrequencyHz: minimum,
              maximumVoiceFrequencyHz: maximum,
            });
          }}
        />
        <XStack className="justify-between text-xs text-foreground-muted">
          <span>Low: {minimumVoiceFrequencyHz.toLocaleString()} Hz</span>
          <span>High: {maximumVoiceFrequencyHz.toLocaleString()} Hz</span>
        </XStack>
      </YStack>
    </SettingsSection.Item>
  );
};

export const VoiceModeSettings = observer(() => {
  // Stop the settings voice preview when this page closes
  useEffect(() => stopVoiceSample, []);
  const selectedVoiceName = getResponseAudioVoiceName(
    settingsStore.responseAudioVoiceId
  );

  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Voice</SettingsSection.Title>
      </SettingsSection.Header>
      <SettingsSection.Item searchId="voice-mode.speech-features">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>Speech Features</SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Show response read-aloud and voice controls
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <SettingsSection.Switch
          aria-label="Enable speech features"
          checked={settingsStore.speechFeaturesEnabled}
          onCheckedChange={(v) => settingsStore.setSpeechFeaturesEnabled(v)}
        />
      </SettingsSection.Item>
      <SettingsSection.Item searchId="voice-mode.assistant-voice">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>Assistant Voice</SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Voice used for read-aloud and spoken responses
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            aria-label={`Assistant voice: ${selectedVoiceName}`}
            disabled={!settingsStore.speechFeaturesEnabled}
          >
            <span>{selectedVoiceName}</span>
            <Icons.chevronDown className="size-3" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            align="end"
            side="bottom"
            className="w-44"
            sideOffset={8}
          >
            {getAvailableResponseAudioVoices(userStore.isInternalUser).map(
              (voice) => (
                <DropdownMenu.Item
                  key={voice.id}
                  onSelect={() => void selectAndSampleVoice(voice.id)}
                >
                  {voice.name}
                </DropdownMenu.Item>
              )
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </SettingsSection.Item>
      <SettingsSection.Item searchId="voice-mode.thinking-sound">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>Thinking Sound</SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Play a soft bird chirp now and then while the assistant thinks or
            uses tools
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <SettingsSection.Switch
          aria-label="Play thinking sound"
          disabled={!settingsStore.speechFeaturesEnabled}
          checked={settingsStore.voiceModeThinkingSoundEnabled}
          onCheckedChange={(v) =>
            settingsStore.setVoiceModeThinkingSoundEnabled(v)
          }
        />
      </SettingsSection.Item>
      <NumericSetting
        searchId="voice-mode.pause-length"
        label="Pause Length (seconds)"
        description="Seconds of silence before what you said submits"
        value={settingsStore.voiceModePauseMs / 1000}
        ariaLabel="Voice pause length in seconds"
        min={VOICE_MODE_RANGES.pauseMs.min / 1000}
        max={VOICE_MODE_RANGES.pauseMs.max / 1000}
        step={0.1}
        onCommit={(seconds) =>
          settingsStore.setVoiceModePauseMs(seconds * 1000)
        }
      />
      <NumericSetting
        searchId="voice-mode.submission-sound-delay"
        label="Submission Sound Delay (seconds)"
        description="Maximum wait for transcription before playing the confirmation sound"
        value={settingsStore.voiceModeSubmittedSoundDelayMs / 1000}
        ariaLabel="Voice submission sound delay in seconds"
        min={VOICE_MODE_RANGES.submittedSoundDelayMs.min / 1000}
        max={VOICE_MODE_RANGES.submittedSoundDelayMs.max / 1000}
        step={0.1}
        onCommit={(seconds) =>
          settingsStore.setVoiceModeSubmittedSoundDelayMs(seconds * 1000)
        }
      />
      <NumericSetting
        searchId="voice-mode.max-recording-time"
        label="Max Recording Time (seconds)"
        description="Maximum length of your Voice Mode messages"
        value={settingsStore.voiceModeMaxRecordingMs / 1000}
        ariaLabel="Voice maximum recording time in seconds"
        min={VOICE_MODE_RANGES.maxRecordingMs.min / 1000}
        max={VOICE_MODE_RANGES.maxRecordingMs.max / 1000}
        step={1}
        onCommit={(seconds) =>
          settingsStore.setVoiceModeMaxRecordingMs(seconds * 1000)
        }
      />
      <NumericSetting
        searchId="voice-mode.speech-volume-threshold"
        label="Speech Volume Threshold (RMS)"
        description="Raise this to ignore more background sound"
        value={settingsStore.voiceModeDetection.speechRmsThreshold}
        ariaLabel="Voice speech volume threshold"
        min={VOICE_MODE_RANGES.speechRmsThreshold.min}
        max={VOICE_MODE_RANGES.speechRmsThreshold.max}
        step={0.001}
        onCommit={(value) =>
          settingsStore.setVoiceModeDetection({ speechRmsThreshold: value })
        }
      />
      <FrequencyRangeSetting />
      <NumericSetting
        searchId="voice-mode.speech-frequency-threshold"
        label="Speech-Frequency Threshold (%)"
        description="Raise this to reject more sound outside speech frequencies"
        value={settingsStore.voiceModeDetection.minimumVoiceScore * 100}
        ariaLabel="Voice speech-frequency threshold percentage"
        min={VOICE_MODE_RANGES.minimumVoiceScore.min * 100}
        max={VOICE_MODE_RANGES.minimumVoiceScore.max * 100}
        step={1}
        onCommit={(percentage) =>
          settingsStore.setVoiceModeDetection({
            minimumVoiceScore: percentage / 100,
          })
        }
      />
    </SettingsSection.Root>
  );
});
